import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import ThemePreviewWindow from '@/components/ThemePreviewWindow';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/context/ThemeContext';
import { ArrowLeft, Check, Palette, Sun, Moon, Sparkles, Type } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function ThemeSettingsPage() {
  const navigate = useNavigate();
  const {
    currentTheme,
    isDarkMode,
    currentFont,
    themes,
    fontFamilies,
    categories,
    getThemesByCategory,
    setTheme,
    setFont,
    toggleDarkMode,
    previewTheme: handlePreviewTheme,
    resetPreview,
    previewFont: handlePreviewFont,
    resetFontPreview,
  } = useTheme();

  const [selectedCategory, setSelectedCategory] = useState('classic');
  const [previewThemeId, setPreviewThemeId] = useState(currentTheme.id);

  const handleThemeChange = (themeObj) => {
    setTheme(themeObj);
    setPreviewThemeId(themeObj.id);
    toast.success(`Theme changed to ${themeObj.name}`);
  };

  const handleFontChange = (fontObj) => {
    setFont(fontObj);
    toast.success(`Font changed to ${fontObj.name}`);
  };

  const handleThemeHover = (themeObj) => {
    handlePreviewTheme(themeObj);
    setPreviewThemeId(themeObj.id);
  };

  const handleThemeGridLeave = () => {
    resetPreview();
    setPreviewThemeId(currentTheme.id);
  };

  // Get themes for selected category
  const categoryThemes = getThemesByCategory(selectedCategory);
  const selectedCategoryData = categories.find(cat => cat.name.toLowerCase() === selectedCategory);

  return (
    <AppShell>
      <div className="flex h-full" data-testid="theme-settings-page">
        {/* Left Side - Theme Selection */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings')}
              className="mb-4"
              data-testid="back-to-settings-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Settings
            </Button>

            <div className="flex items-center gap-3 mb-2">
              <Palette className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-3xl font-bold">Theme Customization</h1>
                <p className="text-muted-foreground">
                  Choose from {themes.length} beautiful themes
                </p>
              </div>
            </div>
          </div>

          {/* Light/Dark Mode Toggle */}
          <Card className="p-6 backdrop-blur-sm bg-card/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">Appearance Mode</h3>
                <p className="text-sm text-muted-foreground">Toggle between light and dark mode</p>
              </div>
              <Button
                onClick={toggleDarkMode}
                variant="outline"
                size="lg"
                className="gap-2"
                data-testid="mode-toggle-btn"
              >
                {isDarkMode ? (
                  <>
                    <Moon className="h-5 w-5" />
                    Dark Mode
                  </>
                ) : (
                  <>
                    <Sun className="h-5 w-5" />
                    Light Mode
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Category Tabs */}
          <div>
            <h3 className="font-semibold mb-3">Browse by Category</h3>
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
              <TabsList className="flex-wrap h-auto gap-2">
                {categories.map(cat => (
                  <TabsTrigger
                    key={cat.name}
                    value={cat.name.toLowerCase()}
                    className="flex-shrink-0"
                  >
                    {cat.name} ({cat.count})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Color Themes by Category */}
          <div>
            <div className="mb-4">
              <h3 className="font-semibold mb-1">
                {selectedCategoryData?.name || 'Themes'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {categoryThemes.length} themes available
              </p>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              onMouseLeave={handleThemeGridLeave}
            >
              {categoryThemes.map((themeObj) => {
                const isActive = currentTheme.id === themeObj.id;

                return (
                  <Card
                    key={themeObj.id}
                    className={cn(
                      'p-4 cursor-pointer transition-all hover:shadow-md group backdrop-blur-sm',
                      isActive ? 'ring-2 ring-primary shadow-lg bg-primary/5' : 'hover:border-primary'
                    )}
                    onClick={() => handleThemeChange(themeObj)}
                    onMouseEnter={() => handleThemeHover(themeObj)}
                    data-testid={`theme-${themeObj.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold flex items-center gap-2">
                          {themeObj.name}
                          {isActive && <Sparkles className="h-3 w-3 text-primary" />}
                        </h4>
                        <p className="text-xs text-muted-foreground">{themeObj.description}</p>
                      </div>
                      {isActive && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Theme Preview Colors */}
                    <div className="flex gap-2">
                      {themeObj.previewColors.map((color, index) => (
                        <div
                          key={index}
                          className="w-10 h-10 rounded-lg border-2 border-background shadow-sm group-hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Typography Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Type className="h-6 w-6 text-primary" />
              <div>
                <h3 className="font-semibold mb-1">Typography</h3>
                <p className="text-sm text-muted-foreground">
                  Choose from {fontFamilies.length} font families
                </p>
              </div>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              onMouseLeave={resetFontPreview}
            >
              {fontFamilies.map((fontObj) => {
                const isActive = currentFont.id === fontObj.id;

                return (
                  <Card
                    key={fontObj.id}
                    className={cn(
                      'p-4 cursor-pointer transition-all hover:shadow-md group backdrop-blur-sm',
                      isActive ? 'ring-2 ring-primary shadow-lg bg-primary/5' : 'hover:border-primary'
                    )}
                    onClick={() => handleFontChange(fontObj)}
                    onMouseEnter={() => handlePreviewFont(fontObj)}
                    data-testid={`font-${fontObj.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold flex items-center gap-2">
                          {fontObj.name}
                          {isActive && <Sparkles className="h-3 w-3 text-primary" />}
                        </h4>
                        <p className="text-xs text-muted-foreground">{fontObj.description}</p>
                      </div>
                      {isActive && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Font Sample */}
                    <div
                      className="text-base py-2 border-t border-border"
                      style={{ fontFamily: fontObj.value }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Info */}
          <Card className="p-4 bg-primary/5 border-primary/20 backdrop-blur-sm">
            <div className="flex gap-3">
              <Palette className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium mb-1">TweakCN Inspired Themes</p>
                <p className="text-muted-foreground">
                  Theme changes are saved automatically and persist across sessions. Hover over any theme to preview it live!
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Side - Live Preview Window */}
        <div className="w-[400px] border-l border-border p-6 overflow-y-auto sticky top-0 h-screen">
          <ThemePreviewWindow
            themeId={previewThemeId}
            mode={isDarkMode ? 'dark' : 'light'}
          />
        </div>
      </div>
    </AppShell>
  );
}
